import * as React from "react";

import { RBACButton } from "../../../molecules/rbac-button";
import { UserEntity } from "../../../../application/domain/user/user-entity";
import { UserError } from "./use-user-basic-info";
import { GroupSelector } from "../../../molecules/group-selector";
import { displayName, Roles } from "../../../../application/domain/rbac/rbac";
import { UserRolesSelector } from "../../../molecules/user-role-selector";
import { TextField } from "@graffer-inc/console-design-kit";

type UserEditProps = {
  inputUserInfo: UserEntity;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  setInputUserInfo: React.Dispatch<React.SetStateAction<UserEntity>>;
  fetchUser: () => Promise<void>;
  updateUser: (user: UserEntity) => Promise<boolean>;
  validationError: UserError;
  shouldSelectGroup: boolean;
  isValidationError: boolean;
  isServiceProvider: boolean;
  isSamlAuthEnabled: boolean;
};

export const UserEdit: React.FC<UserEditProps> = ({
  inputUserInfo,
  setIsEdit,
  setInputUserInfo,
  fetchUser,
  updateUser,
  validationError,
  shouldSelectGroup,
  isValidationError,
  isServiceProvider,
  isSamlAuthEnabled,
}) => {
  return (
    <section className="c-use-detail__section">
      <nav className="level">
        <div className="level-left">
          <h1 className="title is-size-4 mb-4">基本情報</h1>
        </div>
        <div className="level-right">
          <div className="level-item">
            <RBACButton
              requirePermissions="users.update"
              onClick={() => {
                setInputUserInfo(inputUserInfo);
                setIsEdit(false);
              }}
              buttonTypes="secondary"
              buttonText="キャンセル"
            />
          </div>
          <div className="level-item">
            <RBACButton
              requirePermissions="users.update"
              onClick={async () => {
                const isUpdateSuccess = await updateUser(inputUserInfo);
                if (isUpdateSuccess) {
                  fetchUser();
                  setIsEdit(false);
                }
              }}
              disabled={isValidationError}
              buttonTypes="primary"
              buttonText="保存する"
            />
          </div>
        </div>
      </nav>
      <div className="field">
        <TextField
          label="氏名"
          description=""
          placeholder="氏名"
          value={inputUserInfo.name}
          onChange={(e) => {
            setInputUserInfo(
              UserEntity.fromJSON({
                ...inputUserInfo,
                name: e.target.value,
              }),
            );
          }}
          errorMessages={validationError.name}
          required
        />
      </div>
      <div className="field">
        <TextField
          label="アカウント名"
          description=""
          placeholder=""
          value={inputUserInfo.accountName}
          onChange={() => null}
          required
          disabled
        />
      </div>
      <div className="field">
        <TextField
          label="メールアドレス"
          description=""
          placeholder="example@graffer.jp"
          type="email"
          value={inputUserInfo.email}
          onChange={(e) => {
            setInputUserInfo(
              UserEntity.fromJSON({
                ...inputUserInfo,
                emails: [e.target.value],
              }),
            );
          }}
          errorMessages={validationError.email}
        />
      </div>
      <div className="field">
        {/* admin role cannot be changed */}
        {inputUserInfo.roles.includes("Admin") ? (
          <>
            <h2 className="has-text-weight-bold">ロール</h2>
            <div className="control">
              {inputUserInfo.roles.map((role) => (
                <p key={role}>{displayName(role)}</p>
              ))}
            </div>
          </>
        ) : (
          <UserRolesSelector
            label="ロール"
            values={inputUserInfo.roles}
            onChange={(e) => {
              const newRoles = e.target.checked
                ? [...inputUserInfo.roles, e.target.value as Roles]
                : inputUserInfo.roles.filter(
                    (v: Roles) => v !== e.target.value,
                  );
              setInputUserInfo(
                UserEntity.fromJSON({
                  ...inputUserInfo,
                  roles: newRoles,
                  groupsRoles: newRoles.includes("Staff")
                    ? inputUserInfo.groupsRoles
                    : {},
                }),
              );
            }}
            errorMessages={validationError.roles}
            required
          />
        )}
      </div>
      <div className="field">
        <h2 className="has-text-weight-bold">所属グループ</h2>
        {shouldSelectGroup ? (
          <GroupSelector
            editingGroupRoles={inputUserInfo.groupsRoles}
            setGroupRoles={(groupsRoles) => {
              setInputUserInfo(
                UserEntity.fromJSON({
                  ...inputUserInfo,
                  groupsRoles,
                }),
              );
            }}
            validationError={validationError.groupsAndRoles}
            orgId={inputUserInfo.orgId}
            isServiceProvider={isServiceProvider}
          />
        ) : (
          <p className="control">
            特権管理者およびアカウント管理者はグループに所属しません。
          </p>
        )}
      </div>
      <div className="field">
        <h2 className="has-text-weight-bold">2段階認証</h2>
        <div className="control">
          {inputUserInfo.isTOTPEnabled() ? "ON" : "OFF"}
        </div>
      </div>
      <div className="field">
        <h2 className="has-text-weight-bold">ユーザーステータス</h2>
        <div className="control">
          <div className="has-addons">
            <input
              id="userDisableToggle"
              type="checkbox"
              className="switch is-rounded"
              checked={!inputUserInfo.disabled}
              onChange={() => {
                setInputUserInfo(
                  UserEntity.fromJSON({
                    ...inputUserInfo,
                    disabled: !inputUserInfo.disabled,
                  }),
                );
              }}
            />
            <label htmlFor="userDisableToggle">
              {inputUserInfo.disabled ? "無効" : "有効"}
            </label>
          </div>
        </div>
      </div>
      {isSamlAuthEnabled && (
        <>
          <div className="field">
            <h2 className="has-text-weight-bold">認証方法</h2>
            <div className="control">
              <div className="has-addons">
                <p>
                  {inputUserInfo.authMethod === "password"
                    ? "パスワード認証"
                    : "SAML認証"}
                </p>
              </div>
            </div>
          </div>
          {inputUserInfo.authMethod === "saml" && (
            <div className="field">
              <h2 className="has-text-weight-bold">外部ユーザーID</h2>
              <div className="control">
                <div className="has-addons">
                  <p>{inputUserInfo.externalUserId}</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};
